import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal
} from '@angular/core';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { DesignSystemModule } from '@design-system/design-system.module';
import { ToastMessageService } from '@libs/common-services';
import { AgentMutationsService } from '@portal/app/shared/services/queries/agent-mutations.service';

/**
 * Self-contained feedback control for an AI assistant response. One-shot rating —
 * users cannot change or resubmit feedback for the same run id.
 *
 * Three states, derived locally from `#score` + `#pending`:
 *   idle      — neither set; renders both thumbs.
 *   loading   — `#pending` set during in-flight POST; renders the clicked thumb with a spinner.
 *   completed — `#score` set after success; renders only the rated thumb + microcopy.
 *
 * On submission failure the optimistic score is rolled back to null and a toast
 * surfaces the error, returning the control to idle so the user can retry.
 */
@Component({
  selector: 'portal-feedback-thumbs',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DesignSystemModule],
  templateUrl: './feedback-thumbs.component.html',
  host: { class: 'inline-flex items-center gap-1' }
})
export class PortalFeedbackThumbsComponent {
  /** May be undefined briefly between turn-render and finalize metadata —
   * the idle buttons render disabled in that window, then enable once the
   * LangSmith run id lands on the response message. */
  readonly lsRunId = input<string | undefined>();
  readonly clientId = input.required<number>();
  readonly brandId = input.required<number>();

  readonly #mutations = inject(AgentMutationsService);
  readonly #toast = inject(ToastMessageService);
  readonly #mutation = injectMutation(() => this.#mutations.submitFeedback());

  readonly #score = signal<0 | 1 | null>(null);
  readonly #pending = signal<0 | 1 | null>(null);

  protected readonly state = computed<'idle' | 'loading' | 'completed'>(() => {
    if (this.#pending() !== null) return 'loading';
    if (this.#score() !== null) return 'completed';
    return 'idle';
  });

  protected readonly activeIcon = computed(() =>
    this.#score() === 1 ? 'thumb_up' : 'thumb_down'
  );

  protected rate(score: 0 | 1): void {
    const runId = this.lsRunId();
    if (!runId) return;

    const previous = this.#score();
    this.#score.set(score);
    this.#pending.set(score);

    this.#mutation.mutate(
      {
        clientId: this.clientId(),
        brandId: this.brandId(),
        traceRunId: runId,
        score
      },
      {
        onError: () => {
          this.#score.set(previous);
          this.#toast.showToastMessage({
            severity: 'error',
            summary: 'Feedback failed',
            detail: 'Could not submit feedback. Please try again.',
            closable: true
          });
        },
        onSettled: () => this.#pending.set(null)
      }
    );
  }
}
